/*
 * a11y-patches.js — runtime shims for THIRD-PARTY markup we can't edit in Liquid
 * (app blocks, embeds, the Recharge portal). Theme markup is fixed in the theme
 * itself; the only theme-element patch here is the reversible .swipe-mobile
 * keyboard-scroll attribute (it depends on live overflow, which Liquid can't know).
 *
 * Ported (selectively) from the pre-Sleek theme's a11y_patches.js (US-7794/7798):
 *  - Firework <fw-storyblock>/<fw-story>: region + name (the feed labels itself).
 *  - Okendo carousel buttons: accessible names.
 *  - Closeby store-locator iframe: title (re-applied when Closeby re-injects it).
 *  - Recharge portal (/tools/recurring/): valid ARIA id refs, contrast CSS, one H1.
 *  - Backstop: <img> with no alt attribute is treated as decorative (alt="").
 *  - .swipe-mobile sliders: focusable named region only while they overflow.
 *
 * Every patch only fills in what is missing, so a vendor fix upstream wins.
 */
(function () {
  'use strict';
  if (window.__a11yPatchesInit) return;
  window.__a11yPatchesInit = true;

  var IS_RECHARGE = location.pathname.indexOf('/tools/recurring/') === 0;

  function missing(el, name) {
    var v = el.getAttribute(name);
    return v === null || v === '' || v === 'null';
  }
  function setIfMissing(el, name, value) {
    if (missing(el, name)) el.setAttribute(name, value);
  }

  // <fw-embed-feed> is not patched: Firework labels its own inner region (shadow DOM),
  // and a second same-named region on the host fails axe landmark-unique.
  function patchFirework(root) {
    root.querySelectorAll('fw-storyblock, fw-story').forEach(function (el) {
      setIfMissing(el, 'role', 'region');
      setIfMissing(el, 'aria-label', 'Shoppable video');
    });
  }

  function patchOkendo(root) {
    root.querySelectorAll('button[class*="oke-"]').forEach(function (btn) {
      if (btn.hasAttribute('aria-labelledby') || !missing(btn, 'aria-label')) return;
      if ((btn.textContent || '').trim()) return;
      var cls = btn.className || '';
      btn.setAttribute('aria-label', /prev/i.test(cls) ? 'Previous review media' : /next/i.test(cls) ? 'Next review media' : 'Reviews control');
    });
  }

  function patchCloseby(root) {
    root.querySelectorAll('iframe.closeby-inline-embed, [class*="closeby-embed"] iframe').forEach(function (f) {
      if (missing(f, 'title') && missing(f, 'aria-label')) f.setAttribute('title', 'Store locator map');
    });
  }

  function patchMissingAlts(root) {
    root.querySelectorAll('img:not([alt])').forEach(function (img) { img.setAttribute('alt', ''); });
  }

  // Recharge's React app emits ids containing "/" or ":", invalid as ARIA id refs,
  // and aria-controls pointing at tab panels it never renders.
  function patchRechargeIds(root) {
    var fix = function (s) { return (s || '').replace(/[\/:]/g, '-'); };
    root.querySelectorAll('[id]').forEach(function (el) { var f = fix(el.id); if (f !== el.id) el.id = f; });
    ['aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns'].forEach(function (attr) {
      root.querySelectorAll('[' + attr + ']').forEach(function (el) {
        var v = el.getAttribute(attr) || '', f = fix(v);
        if (f !== v) el.setAttribute(attr, f);
      });
    });
    root.querySelectorAll('[aria-controls]').forEach(function (el) {
      var id = el.getAttribute('aria-controls');
      if (id && !document.getElementById(id)) el.removeAttribute('aria-controls');
    });
  }

  var rechargeStyled = false;
  function injectRechargeStyles() {
    if (rechargeStyled) return;
    rechargeStyled = true;
    var s = document.createElement('style');
    s.setAttribute('data-a11y-patch', 'recharge-contrast');
    // Danger (#ee3d11 on its pale fill) -> #b3261e; grey #a1a1a1 -> #767676 (>= 4.5:1).
    s.textContent = [
      '[data-testid="error-message"] { color: #b21a00 !important; }',
      '[data-testid="line-price-compare-at-price"], [id*="tab-inactive"], [id*="tab-Inactive"] { color: #767676 !important; }',
      'button[data-testid*="remove"], button[data-testid*="delete"] { color: #b3261e !important; }'
    ].join('\n');
    (document.head || document.documentElement).appendChild(s);
  }

  var rechargeHeading = false;
  function injectRechargeHeading() {
    if (rechargeHeading || !document.body) return;
    if (document.querySelector('h1')) { rechargeHeading = true; return; }
    var p = location.pathname.toLowerCase();
    var label = p.indexOf('/orders') !== -1 ? 'My Orders'
      : p.indexOf('/payment') !== -1 ? 'Payment Methods'
      : p.indexOf('/account') !== -1 ? 'My Account'
      : p.indexOf('/addresses') !== -1 || p.indexOf('/shipping') !== -1 ? 'My Addresses'
      : p.indexOf('/login') !== -1 ? 'Subscription Portal Login'
      : 'My Subscriptions';
    var h1 = document.createElement('h1');
    h1.className = 'visually-hidden';
    h1.textContent = label;
    document.body.insertBefore(h1, document.body.firstChild);
    rechargeHeading = true;
  }

  // WCAG 2.1.1: theme "swipe-mobile" sliders scroll horizontally on small screens.
  // When (and only when) one overflows, make it a focusable, named region so arrow
  // keys can scroll it; undo when it stops overflowing (e.g. desktop).
  function sectionName(el) {
    var sec = el.closest('.shopify-section, section');
    var h = sec && sec.querySelector('h1, h2, h3');
    var t = h && (h.textContent || '').replace(/\s+/g, ' ').trim();
    return t ? t : 'Scrollable list';
  }
  function patchSwipeRegions(root) {
    root.querySelectorAll('.swipe-mobile').forEach(function (el) {
      var overflows = el.scrollWidth - el.clientWidth > 4;
      var ours = el.hasAttribute('data-a11y-scroll');
      if (overflows && !ours && !el.hasAttribute('tabindex')) {
        var added = ['tabindex'];
        el.setAttribute('tabindex', '0');
        if (missing(el, 'role')) { el.setAttribute('role', 'region'); added.push('role'); }
        if (missing(el, 'aria-label') && !el.hasAttribute('aria-labelledby')) { el.setAttribute('aria-label', sectionName(el)); added.push('aria-label'); }
        el.setAttribute('data-a11y-scroll', added.join(' '));
      } else if (!overflows && ours) {
        el.getAttribute('data-a11y-scroll').split(' ').forEach(function (a) { if (a) el.removeAttribute(a); });
        el.removeAttribute('data-a11y-scroll');
      }
    });
  }

  function safe(fn) { try { fn(); } catch (e) { /* never block the page */ } }

  function run() {
    var root = document;
    safe(function () { patchFirework(root); });
    safe(function () { patchOkendo(root); });
    safe(function () { patchCloseby(root); });
    safe(function () { patchMissingAlts(root); });
    safe(function () { patchSwipeRegions(root); });
    // Firework renders inside shadow roots.
    safe(function () {
      root.querySelectorAll('fw-embed-feed, fw-storyblock, fw-story').forEach(function (host) {
        if (host.shadowRoot) { patchMissingAlts(host.shadowRoot); }
      });
    });
    if (IS_RECHARGE) {
      safe(injectRechargeStyles);
      safe(injectRechargeHeading);
      safe(function () { patchRechargeIds(root); });
    }
  }

  // Re-run (debounced) when apps inject markup later.
  var timer = null;
  function schedule() {
    if (timer) return;
    timer = setTimeout(function () { timer = null; run(); }, 250);
  }

  function start() {
    run();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.__a11yPatchNow = run;
})();
